import React, { useState, useEffect } from 'react';
import { TransactionProgressIndicatorProps, DEFAULT_PROPS } from './types';
import { 
  calculateProgressState, 
  getProgressLabel, 
  getStepAriaLabel,
  announceProgressChange 
} from './utils';
import './TransactionProgressIndicator.css';

export const TransactionProgressIndicator: React.FC<TransactionProgressIndicatorProps> = ({
  transaction,
  variant = DEFAULT_PROPS.variant,
  size = DEFAULT_PROPS.size,
  showLabels = DEFAULT_PROPS.showLabels,
  showIcons = DEFAULT_PROPS.showIcons,
  showPercentage = DEFAULT_PROPS.showPercentage,
  className = '',
  onStepClick
}) => {
  const [focusedStepIndex, setFocusedStepIndex] = useState<number | null>(null);
  const [previousStep, setPreviousStep] = useState<number | null>(null);

  // Calculate progress state
  const progressState = calculateProgressState(transaction);

  // Announce progress changes to screen readers
  useEffect(() => {
    if (previousStep !== null && previousStep !== progressState.currentStep) {
      const currentStepLabel = progressState.steps[progressState.currentStep - 1]?.label || 'Unknown';
      announceProgressChange(
        progressState.currentStep, 
        progressState.steps.length, 
        currentStepLabel
      );
    }
    setPreviousStep(progressState.currentStep);
  }, [progressState.currentStep, progressState.steps, previousStep]);

  // Keyboard navigation handlers
  const handleKeyDown = (event: React.KeyboardEvent, stepIndex: number) => {
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = Math.max(0, stepIndex - 1);
        setFocusedStepIndex(prevIndex);
        // Focus the previous step element
        const prevElement = document.querySelector(
          `.transaction-progress-indicator__step:nth-child(${prevIndex + 1}) .transaction-progress-indicator__step-circle`
        ) as HTMLElement;
        prevElement?.focus();
        break;
        
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = Math.min(progressState.steps.length - 1, stepIndex + 1);
        setFocusedStepIndex(nextIndex);
        // Focus the next step element
        const nextElement = document.querySelector(
          `.transaction-progress-indicator__step:nth-child(${nextIndex + 1}) .transaction-progress-indicator__step-circle`
        ) as HTMLElement;
        nextElement?.focus();
        break;
        
      case 'Home':
        event.preventDefault();
        setFocusedStepIndex(0);
        const firstElement = document.querySelector(
          `.transaction-progress-indicator__step:first-child .transaction-progress-indicator__step-circle`
        ) as HTMLElement;
        firstElement?.focus();
        break;
        
      case 'End':
        event.preventDefault();
        const lastIndex = progressState.steps.length - 1;
        setFocusedStepIndex(lastIndex);
        const lastElement = document.querySelector(
          `.transaction-progress-indicator__step:last-child .transaction-progress-indicator__step-circle`
        ) as HTMLElement;
        lastElement?.focus();
        break;
        
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (onStepClick) {
          const step = progressState.steps[stepIndex];
          onStepClick(step.id, stepIndex);
        }
        break;
    }
  };

  return (
    <div 
      className={`transaction-progress-indicator 
                  transaction-progress-indicator--${variant}
                  transaction-progress-indicator--${size}
                  ${progressState.isDisputed ? 'transaction-progress-indicator--disputed' : ''}
                  ${progressState.isCancelled ? 'transaction-progress-indicator--cancelled' : ''}
                  ${className}`}
      role="progressbar"
      aria-valuenow={progressState.currentStep}
      aria-valuemin={1}
      aria-valuemax={progressState.steps.length}
      aria-label={getProgressLabel(progressState)}
      aria-describedby={`progress-description-${transaction.id}`}
    >
      {/* Screen reader description */}
      <div 
        id={`progress-description-${transaction.id}`}
        className="sr-only"
      >
        Transaction progress indicator showing {progressState.completedSteps} of {progressState.steps.length} steps completed.
        {progressState.isDisputed && ' This transaction is currently disputed.'}
        {progressState.isCancelled && ' This transaction has been cancelled.'}
        {showPercentage && ` Progress: ${Math.round(progressState.progressPercentage)}% complete.`}
      </div>

      <div className="transaction-progress-indicator__container">
        {/* Progress Line with ARIA */}
        <div 
          className="transaction-progress-indicator__line"
          role="presentation"
          aria-hidden="true"
        >
          <div 
            className="transaction-progress-indicator__line-progress"
            style={{ width: `${progressState.progressPercentage}%` }}
            aria-hidden="true"
          />
        </div>
        
        {/* Progress Steps with Navigation */}
        <ol 
          className="transaction-progress-indicator__steps"
          role="list"
          aria-label="Transaction progress steps"
        >
          {progressState.steps.map((step, index) => (
            <li
              key={step.id}
              className={`transaction-progress-indicator__step
                         transaction-progress-indicator__step--${step.status}
                         ${focusedStepIndex === index ? 'transaction-progress-indicator__step--focused' : ''}`}
              role="listitem"
            >
              {/* Step Circle - Interactive if onStepClick provided */}
              <div
                className="transaction-progress-indicator__step-circle"
                role={onStepClick ? "button" : "img"}
                tabIndex={onStepClick ? 0 : -1}
                aria-label={getStepAriaLabel(step, index)}
                aria-current={step.status === 'current' ? 'step' : undefined}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onClick={onStepClick ? () => onStepClick(step.id, index) : undefined}
                onFocus={() => setFocusedStepIndex(index)}
                onBlur={() => setFocusedStepIndex(null)}
              >
                {showIcons ? (
                  <span 
                    className="transaction-progress-indicator__step-icon"
                    aria-hidden="true"
                  >
                    {step.icon}
                  </span>
                ) : (
                  <span 
                    className="transaction-progress-indicator__step-number"
                    aria-hidden="true"
                  >
                    {index + 1}
                  </span>
                )}
              </div>
              
              {/* Step Label with proper semantics */}
              {showLabels && (
                <div className="transaction-progress-indicator__step-label">
                  <span 
                    className="transaction-progress-indicator__step-title"
                    id={`step-title-${transaction.id}-${index}`}
                  >
                    {size === 'small' ? step.shortLabel : step.label}
                  </span>
                  {size === 'large' && (
                    <span 
                      className="transaction-progress-indicator__step-description"
                      id={`step-desc-${transaction.id}-${index}`}
                    >
                      {step.description}
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>

        {/* Optional progress percentage display */}
        {showPercentage && (
          <div className="transaction-progress-indicator__percentage">
            {Math.round(progressState.progressPercentage)}% Complete
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionProgressIndicator;